import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { openableRow } from '../../lib/openableRow'
import { PageBody, PageHeader } from '../../components/PageHeader'
import { dateTimeText, durationText, IncomingRow, QueueItemsResponse, QueueStatus, QueueTab, QueueWait, Rating, WageRow } from './api'
import { StatusPill } from './DashboardPage'
import { EmailsPage } from './EmailsPage'
import styles from './GodMode.module.css'
import own from './EmailsPage.module.css'
import pa from '../ProductActivityPage.module.css'

// Queues: the work Control does in the background, one row per item (API services/god-queues.js), for every client this
// staff member supports. Incoming data is what Crew phones, Field devices and stations sent, waiting for the Cycle Engine,
// failed at its last try (tried again on its own) or done; wage recalculations are waiting or failed, since a done one is
// deleted; Emails is Queued email. Nothing here changes a record; a client can be given priority, which only changes the
// order the Cycle Engine takes clients' incoming data in (turns of 100 records, a priority client's first).

type Tab = QueueTab | 'emails'
const TABS: { key: Tab; label: string }[] = [
  { key: 'incoming', label: 'Incoming data' },
  { key: 'wages', label: 'Wage recalculations' },
  { key: 'emails', label: 'Emails' },
]

const STATUS: Record<QueueStatus, { rating: Rating; label: string }> = {
  waiting: { rating: 'idle', label: 'Waiting' },
  failed: { rating: 'bad', label: 'Failed' },
  done: { rating: 'ok', label: 'Done' },
}

export const TYPE_LABEL: Record<string, string> = {
  PICK: 'Pick',
  PICK_UNDO: 'Pick undone',
  RECEIPT: 'Receipt',
  PREASSIGN: 'Preassign',
  QUALITY: 'Quality',
  BATCH_COMPLETE: 'Batch complete',
}
const typeLabel = (t: string | null) => (t ? TYPE_LABEL[t] || t : '—')
const sourceLabel = (p: string) => (p === 'dataphyll-crew' ? 'Crew app' : p)

interface Filter {
  tenant: string
  status: string
  type: string
  source: string
  from: string
  to: string
  q: string
}
const NO_FILTER: Filter = { tenant: '', status: '', type: '', source: '', from: '', to: '', q: '' }

const query = (tab: QueueTab, f: Filter, page: number) =>
  new URLSearchParams(
    Object.entries({ tab, ...f, q: f.q.trim(), page: String(page) }).filter(([, v]) => v)
  ).toString()

function Detail({ facts, payload }: { facts: [string, React.ReactNode][]; payload?: unknown }) {
  return (
    <div className={own.detail}>
      <dl className={styles.facts}>
        {facts
          .filter(([, v]) => v !== null && v !== undefined && v !== '')
          .map(([k, v]) => (
            <React.Fragment key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </React.Fragment>
          ))}
      </dl>
      {payload !== undefined && <pre className={own.body}>{JSON.stringify(payload, null, 2)}</pre>}
    </div>
  )
}

// A header badge: incoming data's average wait over a period, with how many records it is of.
function WaitBadge({ label, wait }: { label: string; wait: QueueWait }) {
  const slow = wait.avg_ms !== null && wait.avg_ms >= 60000
  return (
    <span className={own.wait} data-testid={`wait-${label}`}>
      <span className={own.tileLabel}>{label}</span>
      <StatusPill status={wait.avg_ms === null ? 'idle' : slow ? 'warn' : 'ok'}>
        {wait.avg_ms === null ? '—' : durationText(wait.avg_ms)}
      </StatusPill>
      <span className={own.sub}>{wait.n.toLocaleString()} record{wait.n === 1 ? '' : 's'}</span>
    </span>
  )
}

export function Waits({ waits }: { waits: QueueItemsResponse['waits'] }) {
  return (
    <div className={own.waits} aria-label="Average wait of incoming data">
      <WaitBadge label="Average wait, past hour" wait={waits.hour} />
      <WaitBadge label="Average wait, today" wait={waits.today} />
    </div>
  )
}

function Items({ tab, onData }: { tab: QueueTab; onData?: (d: QueueItemsResponse) => void }) {
  const { user } = useAuth()
  const [filter, setFilter] = useState<Filter>(NO_FILTER)
  const [words, setWords] = useState('')
  const [page, setPage] = useState(1)
  const [data, setData] = useState<QueueItemsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    try {
      const d = await apiJson<QueueItemsResponse>(user, `/api/god-mode/queue-items?${query(tab, filter, page)}`)
      setData(d)
      onData?.(d)
      setError(null)
    } catch (e: any) {
      setError(e.message)
    }
  }, [user, tab, filter, page, onData])

  const setPriority = async (id: string, on: boolean) => {
    setSaving(true)
    try {
      await apiJson(user, `/api/god-mode/clients/${id}/queue-priority`, { method: 'PUT', body: { priority: on } })
      await load()
    } catch (e: any) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    load()
    // The queues move on their own: keep the page current while it is open.
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, 15000)
    return () => clearInterval(t)
  }, [load])

  // Searching waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilter((f) => (f.q === words ? f : { ...f, q: words }))
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [words])

  const set = (k: keyof Filter) => (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const v = e.target.value
    setFilter((f) => ({ ...f, [k]: v }))
    setPage(1)
  }

  const counts = data?.counts || {}
  const pages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1
  const incoming = tab === 'incoming'
  const chosen = data?.clients.find((c) => c.id === filter.tenant)
  const priorityClients = data?.clients.filter((c) => c.priority) || []

  const tile = (status: QueueStatus) => (
    <button
      key={status}
      type="button"
      className={`${own.tile} ${filter.status === status ? own.tileOn : ''}`}
      aria-pressed={filter.status === status}
      onClick={() => {
        setFilter((f) => ({ ...f, status: f.status === status ? '' : status }))
        setPage(1)
      }}
    >
      <span className={own.tileLabel}>{STATUS[status].label}</span>
      <StatusPill status={counts[status] ? STATUS[status].rating : 'idle'}>{(counts[status] ?? 0).toLocaleString()}</StatusPill>
    </button>
  )

  const status = (s: QueueStatus, attempts: number, err: string | null) => (
    <>
      <StatusPill status={STATUS[s].rating}>{STATUS[s].label}</StatusPill>
      {s !== 'done' && attempts > 0 && <div className={own.sub}>{attempts} attempt{attempts === 1 ? '' : 's'}</div>}
      {s !== 'done' && err && <div className={own.sub}>{err}</div>}
    </>
  )

  const toggle = (id: string) => setOpen((o) => (o === id ? null : id))

  const incomingTable = (rows: IncomingRow[]) => (
    <table className={styles.table} aria-label="Incoming data">
      <thead>
        <tr>
          <th>Received</th>
          <th>Client</th>
          <th>Type</th>
          <th>Tag or item</th>
          <th>Worker</th>
          <th>Source</th>
          <th>Waited</th>
          <th>Took</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const key = `${r.tenant.id}:${r.id}`
          const waited = r.done_at ? Math.max(0, Date.parse(r.done_at) - Date.parse(r.received_at)) : null
          return (
            <React.Fragment key={key}>
              <tr className={own.row} {...openableRow(() => toggle(key))} aria-expanded={open === key} data-testid={`incoming-${r.id}`}>
                <td className={own.nowrap}>{dateTimeText(r.received_at)}</td>
                <td>{r.tenant.name}</td>
                <td><span className={own.badge}>{typeLabel(r.type)}</span></td>
                <td>{r.item_code || '—'}</td>
                <td>{r.worker || '—'}</td>
                <td>{sourceLabel(r.producer_id)}</td>
                <td className={own.nowrap}>{durationText(waited)}</td>
                <td className={own.nowrap}>{durationText(r.process_ms)}</td>
                <td>{status(r.status, r.attempts, r.last_error)}</td>
              </tr>
              {open === key && (
                <tr className={own.open}>
                  <td colSpan={9}>
                    <Detail
                      facts={[
                        ['Device event', r.device_event_id],
                        ['Captured on the device', r.captured_at && dateTimeText(r.captured_at)],
                        ['Work date', r.work_date],
                        ['Done', r.done_at && dateTimeText(r.done_at)],
                        ['Waited, received to done', waited === null ? null : durationText(waited)],
                        [r.status === 'done' ? 'Processing took' : 'Last try took', r.process_ms === null ? null : durationText(r.process_ms)],
                        ['Last try', r.last_attempt_at && dateTimeText(r.last_attempt_at)],
                        ['Last error', r.last_error],
                      ]}
                      payload={r.payload}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          )
        })}
      </tbody>
    </table>
  )

  const wageTable = (rows: WageRow[]) => (
    <table className={styles.table} aria-label="Wage recalculations">
      <thead>
        <tr>
          <th>Requested</th>
          <th>Client</th>
          <th>Worker</th>
          <th>Work date</th>
          <th>Reason</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const key = `${r.tenant.id}:${r.id}`
          return (
            <React.Fragment key={key}>
              <tr className={own.row} {...openableRow(() => toggle(key))} aria-expanded={open === key}>
                <td className={own.nowrap}>{dateTimeText(r.requested_at)}</td>
                <td>{r.tenant.name}</td>
                <td>{r.worker || '—'}</td>
                <td>{r.work_date || '—'}</td>
                <td>{r.reason || '—'}</td>
                <td>
                  {status(r.status, r.attempt_count, r.error)}
                  {r.state === 'processing' && <div className={own.sub}>being worked on</div>}
                </td>
              </tr>
              {open === key && (
                <tr className={own.open}>
                  <td colSpan={6}>
                    <Detail
                      facts={[
                        ['Requested by', r.requested_by],
                        ['Started', r.processing_at && dateTimeText(r.processing_at)],
                        ['Attempts', r.attempt_count],
                        ['Error', r.error],
                      ]}
                    />
                  </td>
                </tr>
              )}
            </React.Fragment>
          )
        })}
      </tbody>
    </table>
  )

  return (
    <div className={styles.stack}>
      <div className={own.filters}>
        <label className={styles.field}>
          Client
          <select value={filter.tenant} onChange={set('tenant')}>
            <option value="">All clients</option>
            {data?.clients.map((c) => (
              <option key={c.id} value={c.id}>{c.priority ? `${c.name} (priority)` : c.name}</option>
            ))}
          </select>
        </label>
        {chosen && (
          <button
            type="button"
            className={styles.secondary}
            disabled={saving}
            aria-pressed={chosen.priority}
            onClick={() => setPriority(chosen.id, !chosen.priority)}
          >
            {chosen.priority ? 'Take off priority' : 'Give priority'}
          </button>
        )}
        <label className={styles.field}>
          Status
          <select value={filter.status} onChange={set('status')}>
            <option value="">All</option>
            {(data?.statuses || (incoming ? ['waiting', 'failed', 'done'] : ['waiting', 'failed'])).map((s) => (
              <option key={s} value={s}>{STATUS[s].label}</option>
            ))}
          </select>
        </label>
        {incoming && (
          <>
            <label className={styles.field}>
              Type
              <select value={filter.type} onChange={set('type')}>
                <option value="">All types</option>
                {(data?.types || Object.keys(TYPE_LABEL)).map((t) => (
                  <option key={t} value={t}>{typeLabel(t)}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Source
              <select value={filter.source} onChange={set('source')}>
                <option value="">All sources</option>
                {data?.producers.map((p) => (
                  <option key={p} value={p}>{sourceLabel(p)}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              Done from
              <input type="date" value={filter.from} max={filter.to || undefined} onChange={set('from')} />
            </label>
            <label className={styles.field}>
              Done to
              <input type="date" value={filter.to} min={filter.from || undefined} onChange={set('to')} />
            </label>
          </>
        )}
        <label className={`${styles.field} ${own.search}`}>
          Search
          <input
            type="search"
            value={words}
            onChange={(e) => setWords(e.target.value)}
            placeholder={incoming ? 'Tag, worker, device event or error' : 'Worker, reason or error'}
            maxLength={200}
          />
        </label>
      </div>

      {priorityClients.length > 0 && (
        <p className={own.sub} data-testid="priority-clients">
          Priority: {priorityClients.map((c) => c.name).join(', ')}. Their incoming data is taken ahead of other clients'.
        </p>
      )}

      {data && <div className={own.tiles}>{data.statuses.map(tile)}</div>}

      {error && <p className={styles.error}>{error}</p>}
      {!data && !error && <p className={styles.lead}>Loading…</p>}

      {data && (
        <>
          <h2 className={own.heading}>
            {TABS.find((t) => t.key === tab)!.label} ({data.total.toLocaleString()})
          </h2>
          <div className={styles.tableCard}>
            {data.items.length === 0 ? (
              <div className={styles.empty}>Nothing matches.</div>
            ) : incoming ? (
              incomingTable(data.items as IncomingRow[])
            ) : (
              wageTable(data.items as WageRow[])
            )}
          </div>
          {pages > 1 && (
            <div className={own.pager}>
              <button type="button" className={styles.secondary} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Previous
              </button>
              <span>Page {page} of {pages}</span>
              <button type="button" className={styles.secondary} disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                Next
              </button>
            </div>
          )}
        </>
      )}
      <p className={own.sub}>
        {incoming
          ? "Waiting and failed records are listed whenever they came in; the dates choose which done ones show, by the client's own day they were received (today when left empty). A failed record is tried again on its own."
          : 'A wage recalculation is deleted once it is done, so only waiting and failed ones are here.'}{' '}
        Clients take turns of 100 incoming records each, so one that sends thousands at once does not hold the others
        up; choose a client to give it priority, and its records are taken first.
        Updated every 15 seconds.
      </p>
    </div>
  )
}

export function QueuesPage() {
  const [params, setParams] = useSearchParams()
  const tab: Tab = TABS.some((t) => t.key === params.get('tab')) ? (params.get('tab') as Tab) : 'incoming'
  // The averages come with each read of the list, whichever tab and filters it is; Emails reads none.
  const [waits, setWaits] = useState<QueueItemsResponse['waits'] | null>(null)
  const onData = useCallback((d: QueueItemsResponse) => setWaits(d.waits), [])
  return (
    <>
      <PageHeader title="Queues">{tab !== 'emails' && waits && <Waits waits={waits} />}</PageHeader>
      <PageBody>
        <div className={styles.stack}>
          <div className={pa.tabTrack} role="tablist" aria-label="Queues">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? pa.tabOn : pa.tab}
                onClick={() => setParams(t.key === 'incoming' ? {} : { tab: t.key }, { replace: true })}
              >
                {t.label}
              </button>
            ))}
          </div>
          {tab === 'emails' ? <EmailsPage embedded /> : <Items key={tab} tab={tab} onData={onData} />}
        </div>
      </PageBody>
    </>
  )
}
